import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  notificationPanelTitle,
  resolveNotificationModuleParam,
} from '@/config/notificationModuleScope';
import { moduleNotificationsPath } from '@/config/moduleWorkPaths';
import { useActiveModule } from '@/hooks/useActiveModule';
import { useNotificationInbox } from '@/hooks/useNotificationInbox';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  NotificationInboxEmpty,
  NotificationInboxItem,
} from '@/components/layout/NotificationInboxItem';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function NotificationDropdown({ variant = 'default' }: { variant?: 'default' | 'header' }) {
  const location = useLocation();
  const activeModule = useActiveModule();
  const moduleParam = resolveNotificationModuleParam(location.pathname, activeModule);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<'read' | 'clear-read' | 'clear-all' | null>(null);
  const panelTitle = notificationPanelTitle(moduleParam);
  const notificationsPath = moduleNotificationsPath(activeModule ?? 'POS');
  const { items, unread, loading, load, markRead, markAllRead, clearRead, clearAll } =
    useNotificationInbox({ module: moduleParam, size: 10 });

  const hasItems = items.length > 0;
  const allRead = hasItems && unread === 0;
  const readInPanel = items.filter((n) => n.readFlag === true).length;

  const runAction = async (
    action: 'read' | 'clear-read' | 'clear-all',
    fn: () => Promise<void>,
    success: string,
  ) => {
    setBusy(action);
    try {
      await fn();
      notify.success(success);
    } catch (err) {
      showApiError(err, 'Notification action failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <Tooltip content={panelTitle}>
        <button
          type="button"
          className={
            variant === 'header' ? 'workspace-header-ghost-btn relative' : 'icon-btn icon-btn-md relative'
          }
          onClick={() => {
            setOpen((value) => !value);
            if (!open) void load();
          }}
          aria-label={panelTitle}
          aria-expanded={open}
        >
          <NotificationsIcon fontSize={variant === 'header' ? 'medium' : 'small'} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </Tooltip>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-2 w-[22rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{panelTitle}</p>
                {unread > 0 ? (
                  <p className="text-xs text-slate-500">{unread} unread</p>
                ) : hasItems ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">All read</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {unread > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
                    disabled={busy !== null}
                    onClick={() =>
                      void runAction('read', markAllRead, 'All notifications marked as read')
                    }
                  >
                    {busy === 'read' ? 'Updating…' : 'Mark all read'}
                  </button>
                )}
                {allRead && (
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-600 hover:underline disabled:opacity-50 dark:text-slate-300"
                    disabled={busy !== null}
                    onClick={() =>
                      void runAction('clear-all', clearAll, 'Notifications cleared')
                    }
                  >
                    {busy === 'clear-all' ? 'Clearing…' : 'Clear all'}
                  </button>
                )}
                {!allRead && readInPanel > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-500 hover:underline disabled:opacity-50"
                    disabled={busy !== null}
                    onClick={() =>
                      void runAction('clear-read', clearRead, 'Read notifications cleared')
                    }
                  >
                    {busy === 'clear-read' ? 'Clearing…' : 'Clear read'}
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading && (
                <div className="space-y-0">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="animate-pulse border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <div className="mb-2 h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
                      <div className="h-2 w-full rounded bg-slate-100 dark:bg-slate-800" />
                    </div>
                  ))}
                </div>
              )}
              {!loading && !hasItems && (
                <NotificationInboxEmpty moduleScoped={Boolean(moduleParam)} />
              )}
              {!loading &&
                items.map((notification) => (
                  <NotificationInboxItem
                    key={notification.id}
                    notification={notification}
                    compact
                    onOpen={() => {
                      if (notification.readFlag !== true) {
                        void markRead(notification.id);
                      }
                    }}
                  />
                ))}
            </div>

            <div className="border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800/50">
              <Link
                to={notificationsPath}
                className="text-xs font-semibold text-primary-600 hover:underline dark:text-primary-400"
                onClick={() => setOpen(false)}
              >
                View all notifications →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
